import { Feature } from 'geojson';
import { inject, injectable, interfaces } from 'inversify';
import { cloneDeep } from 'lodash/fp';
import { action, makeObservable, toJS, when } from 'mobx';
import { v4 } from 'uuid';
import { fixPseudoRectangle, fixPseudoRectangleGeometry } from '../../helpers/geometry/geometryFixes.helpers';
import { buildBounds, getLatLngsForGeojson } from '../../helpers/geometry/polygon.helpers';
import { ACDLError, InputStatus } from '../../models/error.model';
import { IEventBus } from '../../services/eventBus.service';
import { ImageSetType } from '../projectDetails/sub/images/projectDetailsImages.model';
import { IProjectDetailsImagesApiService } from '../projectDetails/sub/images/services/projectDetailsImagesApi.service';
import { IAnnotationApiService } from './annotationApi.service';
import { IImage, ISegmentation, ISegmentationFeature, SegmentationGeometry } from './annotations.interface';
import { IAnnotationsStore, IHiddenSegmentation } from './annotations.store';
import { IAnnotationUiStore } from './annotationUi.store';
import { cleanClone, cleanClones, ImageScopeName, IQuestionModel, validateQuestion } from './question.model';
import { IAnnotationTypeBl } from './submodules/annotationTypes/annotationType.bl';
import { IAnnotationTypeStore } from './submodules/annotationTypes/annotationType.store';
import { AnnotationToolType } from './submodules/annotationTypes/models/annotationToolType';
import { IAnnotationType } from './submodules/annotationTypes/models/annotationType';
import { SegmentationDeselectedEvent } from './submodules/segmentations/events/segmentationDeselectedEvent';
import { SegmentationSelectedEvent } from './submodules/segmentations/events/segmentationSelectedEvent';
import { SegmentationDeletedEvent } from './submodules/segmentations/events/sementationDeletedEvent';

export interface IAnnotationService {
  changedModeFromReviewToCorrect(): void;
  setQuestionsAsCurrent(questions: IQuestionModel[]): void;
  createNewSegmentation(activeAnnotationTypeId: string, annotation: Feature): ISegmentation;
  addNewSegmentation(annotation: ISegmentation): void;
  selectSegmentation(id: string): void;
  deselectSegmentation(): void;
  getNextSegmentation(): ISegmentation | undefined;
  getPreviousSegmentation(): ISegmentation | undefined;
  moveToNextInvalidSegmentation(): void;
  copySegmentation(id: string): void;
  pasteSegmentation(annotation: Feature, id: string): void;
  updateSegmentation(id: string, annotation: Feature): void;
  deleteSegmentation(id: string): void;
  deleteSegmentations(annotations: ISegmentation[]): void;
  getAnnotationType(id: string): IAnnotationType | undefined;
  getAnnotationTypeColor(annotationTypeId: string): string;
  getSegmentations(): ISegmentation[];
  setSegmentations(segmentations: ISegmentation[]): void;
  clear(): void;
  clearAnnotations(): void;
  clearAnswers(): void;
  validateAnnotations(): boolean;
  fixRectangles(): void;
  enableEdit(): void;
  getProjectAnnotationInfoAsync(projectId: string): Promise<void>;
  showSegmentation(featureId: string, segmentationId?: string): void;
  hideSegmentation(featureId: string, segmentationId?: string): void;
  hideSegmentations(segmentationsToHide: IHiddenSegmentation[]): void;
  updateProjectImageSetAsync(imageId: string, setType: ImageSetType): Promise<void | ACDLError>;
}

export namespace IAnnotationService {
  export const $: interfaces.ServiceIdentifier<IAnnotationService> = Symbol('ANNOTATION_SERVICE');
}

interface ISegmentationFeatureWithProperties extends ISegmentationFeature {
  properties: {
    annotationTypeId: string;
    featureType: AnnotationToolType;
  };
}

@injectable()
export class AnnotationService implements IAnnotationService {
  constructor(
    @inject(IAnnotationUiStore.$) public readonly uiStore: IAnnotationUiStore,
    @inject(IAnnotationTypeStore.$) private readonly annotationTypeStore: IAnnotationTypeStore,
    @inject(IAnnotationTypeBl.$) private readonly annotationTypeBl: IAnnotationTypeBl,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(IAnnotationsStore.$) private readonly annotationsStore: IAnnotationsStore,
    @inject(IAnnotationApiService.$) private annotationApiService: IAnnotationApiService,
    @inject(IProjectDetailsImagesApiService.$) private readonly projectDetailsImagesApiService: IProjectDetailsImagesApiService
  ) {
    makeObservable(this);
    this.annotationTypeBl.onDeselectSegmentationInKeyBinding = this.deselectSegmentation;
    this.annotationTypeBl.whenImageIsLoaded = async (): Promise<IImage> => {
      await when(() => !!this.annotationsStore.image);

      if (!this.annotationsStore.image) {
        throw 'something went wrong';
      }

      return this.annotationsStore.image;
    };
  }

  @action.bound
  async updateProjectImageSetAsync(imageId: string, setType: ImageSetType): Promise<void | ACDLError> {
    if (!this.annotationsStore.image) {
      return;
    }

    this.annotationsStore.image.imageSet = setType;

    await this.projectDetailsImagesApiService.updateProjectImageSetAsync({
      projectId: this.annotationsStore.projectId,
      filterId: '',
      toggledImages: [imageId],
      selectionMode: 'Deselect',
      projectImageSet: setType,
    });
  }

  @action
  setQuestionsAsCurrent = (questions: IQuestionModel[]): void => {
    this.annotationsStore.currentQuestions.replace(questions);
  };

  @action.bound
  createNewSegmentation(activeAnnotationTypeId: string, annotation: Feature): ISegmentation {
    const annotationType = this.getAnnotationType(activeAnnotationTypeId);

    annotation.properties = {
      annotationTypeId: activeAnnotationTypeId,
      featureType: annotationType?.selectorType,
    };

    const segmentationFeature = {
      ...annotation,
      color: this.getAnnotationTypeColor(activeAnnotationTypeId),
      featureType: annotationType?.selectorType,
      id: v4(),
    } as ISegmentationFeatureWithProperties;

    const feature = fixPseudoRectangle(segmentationFeature);
    const latlngs = getLatLngsForGeojson(feature);

    return {
      feature,
      latlngs,
      questions: this.getAnnotationTypeQuestions(activeAnnotationTypeId),
      bbox: buildBounds(latlngs),
      priority: this.annotationsStore.segmentations.length + 1,
    };
  }

  @action
  addNewSegmentation = (segmentation: ISegmentation): void => {
    segmentation.feature = fixPseudoRectangle(segmentation.feature);
    segmentation.latlngs = getLatLngsForGeojson(segmentation.feature);
    segmentation.bbox = buildBounds(segmentation.latlngs);

    this.annotationsStore.setSegmentations([...this.annotationsStore.segmentations, segmentation]);
    if (segmentation.questions.length > 0) {
      this.selectSegmentation(segmentation.feature.id);
      this.annotationsStore.drawFocusFrameForId = segmentation.feature.id;
    }

    this.showSegmentation(segmentation.feature.id, segmentation.id);
    this.resetValidateAnnotations();
  };

  @action
  updateSegmentation = (id: string, feature: Feature<SegmentationGeometry>): void => {
    const segmentation = this.annotationsStore.segmentations.find((s) => s.feature.id === id);
    if (!segmentation) {
      return;
    }

    segmentation.feature.geometry = feature.geometry;
    segmentation.feature = fixPseudoRectangle(segmentation.feature);
    segmentation.latlngs = getLatLngsForGeojson(segmentation.feature);
    segmentation.bbox = buildBounds(segmentation.latlngs);
  };

  @action.bound
  enableEdit(): void {
    this.annotationsStore.drawFocusFrameForId = undefined;
  }

  @action.bound
  selectSegmentation(id: string): void {
    const segmentation = this.annotationsStore.segmentations.find((s) => s.feature.id === id);

    if (segmentation) {
      if (this.annotationsStore.selectedSegmentation?.feature.id === segmentation.feature.id) {
        return;
      }
      this.annotationsStore.selectedSegmentation = segmentation;
      this.eventBus.sendEvent(new SegmentationSelectedEvent(this.annotationsStore.selectedSegmentation));
    } else {
      this.deselectSegmentation();
    }
    this.annotationsStore.invalidSegmentation = undefined;
  }

  @action
  deselectSegmentation = (): void => {
    this.annotationsStore.selectedSegmentation = undefined;
    this.annotationsStore.invalidSegmentation = undefined;
    this.eventBus.sendEvent(new SegmentationDeselectedEvent());
  };

  @action.bound
  getNextSegmentation(): ISegmentation | undefined {
    const segmentationsWithQuestions = this.annotationsStore.segmentations
      .filter((s) => s.questions.length > 0)
      .filter((s) => this.annotationTypeStore.annotationTypesOptions.some((a) => a.id === s.feature.properties?.annotationTypeId && a.isVisible));

    if (segmentationsWithQuestions.length === 0) {
      return undefined;
    }

    if (this.annotationsStore.selectedSegmentation === undefined) {
      return segmentationsWithQuestions[0];
    }

    const nextSegmentationIndex =
      (segmentationsWithQuestions.findIndex((s) => s.feature.id === this.annotationsStore.selectedSegmentation?.feature.id) + 1) %
      segmentationsWithQuestions.length;
    return segmentationsWithQuestions[nextSegmentationIndex];
  }

  @action.bound
  getPreviousSegmentation(): ISegmentation | undefined {
    const segmentationsWithQuestions = this.annotationsStore.segmentations
      .filter((s) => s.questions.length > 0)
      .filter((s) => this.annotationTypeStore.annotationTypesOptions.some((a) => a.id === s.feature.properties?.annotationTypeId && a.isVisible));

    if (segmentationsWithQuestions.length === 0) {
      return undefined;
    }

    if (this.annotationsStore.selectedSegmentation === undefined) {
      return segmentationsWithQuestions[0];
    }

    let previousSegmentationIndex = segmentationsWithQuestions.findIndex((s) => s.feature.id === this.annotationsStore.selectedSegmentation?.feature.id) - 1;
    if (previousSegmentationIndex === -1) {
      previousSegmentationIndex = segmentationsWithQuestions.length - 1;
    }

    return segmentationsWithQuestions[previousSegmentationIndex];
  }

  @action.bound
  moveToNextInvalidSegmentation(): void {
    this.annotationTypeBl.handleMoveToNextSegmentation();
    const invalidSegmentation = this.annotationsStore.segmentations.find((s) => s.questions.some((q) => q.status.isValid === false));

    if (!invalidSegmentation) {
      return;
    }

    this.annotationsStore.drawFocusFrameForId = invalidSegmentation.feature.id;
    this.selectSegmentation(invalidSegmentation.feature.id);
    this.annotationsStore.invalidSegmentation = invalidSegmentation;
  }

  @action.bound
  copySegmentation(id: string): void {
    this.annotationsStore.copiedSegmentation = toJS(this.annotationsStore.segmentations.find((s) => s.feature.id === id));
  }

  @action.bound
  pasteSegmentation(annotation: Feature<SegmentationGeometry>, id: string): void {
    if (!this.annotationsStore.copiedSegmentation) {
      return;
    }

    if (
      this.annotationTypeStore.annotationTypes.find((a) => a.id === this.annotationTypeStore.activeAnnotationTypeId)?.selectorType === AnnotationToolType.BRUSH
    ) {
      return;
    }

    const feature = {
      ...annotation,
      id,
      properties: { ...this.annotationsStore.copiedSegmentation.feature.properties },
      color: this.annotationsStore.copiedSegmentation.feature.color,
      featureType: this.annotationsStore.copiedSegmentation.feature.featureType,
    };

    const latlngs = getLatLngsForGeojson(feature);

    this.annotationsStore.setSegmentations([
      ...this.annotationsStore.segmentations,
      {
        feature,
        latlngs,
        questions: cleanClones(this.annotationsStore.copiedSegmentation.questions),
        bbox: buildBounds(latlngs),
        priority: this.annotationsStore.segmentations.length + 1,
      },
    ]);

    this.selectSegmentation(id);
  }

  @action.bound
  deleteSegmentation(id: string): void {
    if (this.annotationsStore.selectedSegmentation && this.annotationsStore.selectedSegmentation.feature.id === id) {
      this.deselectSegmentation();
    }
    this.annotationsStore.setSegmentations(this.annotationsStore.segmentations.filter((s) => s.feature.id !== id));
    this.eventBus.sendEvent(new SegmentationDeletedEvent(id));
  }

  @action.bound
  deleteSegmentations(annotations: ISegmentation[]): void {
    this.setSegmentations(this.annotationsStore.segmentations.filter((s) => annotations.includes(s)));
  }

  changedModeFromReviewToCorrect(): void {
    this.deselectSegmentation();
  }

  @action.bound
  validateAnnotations(): boolean {
    this.uiStore.setIsInValidation(true);
    const imageQuestions = this.annotationsStore.questions.filter((q) => q.scopes.includes(ImageScopeName));
    const other = this.annotationsStore.questions.filter((q) => !q.scopes.includes(ImageScopeName));
    const segmentationsQuestions = this.annotationsStore.segmentations
      .map((s) => s.questions)
      .reduce((previousQuestions: IQuestionModel[], currentQuestions: IQuestionModel[]) => previousQuestions.concat(currentQuestions), []);

    imageQuestions.forEach((q) => validateQuestion(q));
    segmentationsQuestions.forEach((q) => validateQuestion(q));

    this.annotationsStore.questions.replace([...imageQuestions, ...other]);

    const imageQuestionsAreValid = imageQuestions.every((q) => q.status.isValid !== false);
    const segmentationsQuestionsAreValid = segmentationsQuestions.every((q) => q.status.isValid !== false);

    if (!imageQuestionsAreValid) {
      this.deselectSegmentation();
    } else if (!segmentationsQuestionsAreValid) {
      this.moveToNextInvalidSegmentation();
    }

    return imageQuestionsAreValid && segmentationsQuestionsAreValid && !!this.annotationsStore.image;
  }

  @action.bound
  resetValidateAnnotations(): void {
    const imageQuestions = this.annotationsStore.questions.filter((q) => q.scopes.includes(ImageScopeName) && q.status.isValid === false);
    const segmentationsQuestions = this.annotationsStore.segmentations
      .map((s) => s.questions)
      .reduce((previousQuestions: IQuestionModel[], currentQuestions: IQuestionModel[]) => previousQuestions.concat(currentQuestions), [])
      .filter((q) => q.status.isValid === false);

    imageQuestions.forEach((q) => {
      q.status = InputStatus.empty();
    });
    segmentationsQuestions.forEach((q) => {
      q.status = InputStatus.empty();
    });
    this.uiStore.setIsInValidation(false);
  }

  getAnnotationType(id: string): IAnnotationType | undefined {
    return this.annotationTypeStore.annotationTypes.find((t) => t.id === id);
  }

  getAnnotationTypeColor(annotationTypeId: string): string {
    const type = this.annotationTypeStore.annotationTypes.find((m) => m.id === annotationTypeId);
    if (type) {
      return type.color;
    }
    return 'black';
  }

  getAnnotationTypeQuestions(id: string): IQuestionModel[] {
    return this.annotationsStore.questions.filter((q) => q.scopes.map((s) => s.toUpperCase()).includes(id.toUpperCase())).map((q) => cleanClone(q));
  }

  @action.bound
  getSegmentations(): ISegmentation[] {
    return cloneDeep(this.annotationsStore.segmentations.concat());
  }

  @action.bound
  setSegmentations(segmentations: ISegmentation[]): void {
    this.annotationsStore.setSegmentations(segmentations);
    if (this.annotationsStore.selectedSegmentation && !segmentations.find((s) => s.feature.id === this.annotationsStore.selectedSegmentation?.feature.id)) {
      this.deselectSegmentation();
    }
    this.uiStore.forceReRender();
  }

  @action
  clear(): void {
    this.annotationsStore.id = undefined;
    this.annotationsStore.image = undefined;
    this.annotationsStore.selectedSegmentation = undefined;
    this.annotationsStore.invalidSegmentation = undefined;
    this.annotationsStore.questions.length = 0;
    this.annotationsStore.setSegmentations([]);
  }

  @action
  clearAnnotations(): void {
    this.annotationsStore.id = undefined;
    this.annotationsStore.selectedSegmentation = undefined;
    this.annotationsStore.invalidSegmentation = undefined;
    this.annotationsStore.setSegmentations([]);
  }

  @action
  clearAnswers(): void {
    this.annotationsStore.questions.forEach((q) => {
      q.answers.forEach((a) => (a.selected = false));
      q.answer = undefined;
      q.status = InputStatus.empty();
    });
  }

  @action
  fixRectangles(): void {
    this.annotationsStore.segmentations.forEach((s) => {
      if (s.feature.featureType === AnnotationToolType.RECTANGLE) {
        s.feature.geometry = fixPseudoRectangleGeometry(s.feature.geometry);
      }
    });
  }

  @action
  async getProjectAnnotationInfoAsync(projectId: string): Promise<void> {
    const result = await this.annotationApiService.getProjectAnnotationInfoAsync(projectId);
    this.annotationsStore.setProjectInfo(result.projectStatus, result.projectRole);
  }

  @action
  showSegmentation(featureId: string, segmentationId?: string): void {
    let newHiddenSegmentations: IHiddenSegmentation[] = this.annotationsStore.hiddenSegmentations.slice();

    newHiddenSegmentations = newHiddenSegmentations.filter((s) => s.featureId === undefined || s.featureId !== featureId);

    if (segmentationId !== undefined) {
      newHiddenSegmentations = newHiddenSegmentations.filter((s) => s.segmentationId === undefined || s.segmentationId !== segmentationId);
    }

    this.annotationsStore.setHiddenSegmentations(newHiddenSegmentations);
  }

  @action
  hideSegmentation(featureId: string, segmentationId?: string): void {
    const newItems: IHiddenSegmentation[] = this.annotationsStore.hiddenSegmentations.slice();
    newItems.push({ segmentationId, featureId });
    this.annotationsStore.setHiddenSegmentations(newItems);
  }

  @action
  hideSegmentations(segmentationsToHide: IHiddenSegmentation[]): void {
    const newItems: IHiddenSegmentation[] = this.annotationsStore.hiddenSegmentations.slice().concat(segmentationsToHide);
    this.annotationsStore.setHiddenSegmentations(newItems);
  }
}
